import { useQuery, skipToken } from '@tanstack/react-query';
import { isPermissionRefused, type LockerLog } from '@citibox-panel/api-client';

export interface UseLockerLog {
  log: LockerLog | undefined;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
}

// The box activity log, keyed by BOX id (contract update 2026-07-27: the log is read box-first,
// `GET /boxes/<id>/logs` — the caller resolves the box from the delivery detail's `box.id`). The
// query is skipped (skipToken) until the section is unfolded AND a box is known — so opening the
// «Entrega» detail costs no request and the first unfold is the lazy fetch. The hook stays MOUNTED
// while the delivery has a «buzón» (even collapsed), so its cache/observer survive a
// collapse→expand and a re-unfold shows the cached log instantly while a background refetch
// revalidates. Unlike the detail query it does NOT throwOnError — a log failure stays inline
// (retry) so the rest of the detail keeps working. Mock-first: the real endpoint needs app-service
// to validate the Firebase token.
export function useLockerLog(
  boxId: string | undefined,
  open: boolean,
  fetchLockerLog: (boxId: string) => Promise<LockerLog>,
): UseLockerLog {
  const { data, isPending, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['locker-log', boxId],
    queryFn: open && boxId !== undefined ? () => fetchLockerLog(boxId) : skipToken,
  });
  // A permission refusal is the host's to show: it replaces the whole view, so reporting it here
  // as a failed read would only paint the inline strip under the access screen.
  return {
    log: data,
    isPending,
    isFetching,
    isError: isError && !isPermissionRefused(error),
    refetch: () => void refetch(),
  };
}
